import { useEffect, useState, type ReactElement, type ReactNode } from "react"
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "src/app/store";
import Spinner from "src/components/Spinner";
import { EndUserInstanceType } from "../../types/EndUserInstanceType";
import { getEndUserInstanceLocationsByEndUserInstanceID, insertEndUserInstanceLocation, deleteEndUserInstanceLocation } from "src/features/endUserInstance/hooks/endUserInstanceLocations";
import EndUserInstanceLocationList from "src/features/endUserInstance/components/Main/EndUserInstanceLocationList";
import { EndUserInstanceLocationType } from "src/features/endUserInstance/types/EndUserInstanceLocationType";
import EndUserInstanceLocationModal from "src/features/endUserInstance/components/Main/EndUserInstanceLocationModal";
import { createEndUserInstanceLocation, updateEndUserInstanceLocation } from "src/features/endUserInstance/hooks/endUserInstanceLocation";
import { endUserInstanceLocationReset } from "src/features/endUserInstance/hooks/endUserInstanceLocation";
import { MACSearchResultType } from "src/features/dealers/types/MACSearchResultType";
import DeleteModal from "src/components/DeleteModal";
import ResetAdminPasswordModal from "./ResetAdminPasswordModal";
import { ResetAdminPasswordType } from "../../types/ResetAdminPasswordType";
import { apiClient } from "src/utils/Api/apiClient";
import CryptoJS from "crypto-js";
import OTPModal from "./OTPModal";
import { OTPType } from "../../types/OTPType";
import { OTPCodeType } from "../../types/OTPCodType";

export interface EndUserInstanceListProps {
        endUserInstanceClick: (event: React.MouseEvent<HTMLDivElement>) => void,
        deleteEndUserInstanceClick: (event: React.MouseEvent<HTMLDivElement>) => void,
        endUserInstanceList: Array<EndUserInstanceType>,
        macAddressSearchResult: MACSearchResultType,
        children?: ReactNode
}

const initialEndUserInstance = {
        Id: 0,
        EndUserInstanceId: "",
        EndUserId: 0,
        Name: "",
        NumberOfDoors: 0,
        Enabled: false,
        Notes: "",
        Link: "",
        CreatedAt: "",
}

const initialResetAdminPassword = {
        Id: 0,
        Password: "",
        ConfirmPassword: ""
}

const initialEndUserInstanceLocation = {
        Id: 0,
        EndUserInstanceLocationId: "",
        EndUserInstanceId: 0,
        LocationName: "",
        NumberOfDoors: 0,
        LocationNumber: null,
        MACAddress: "",
        SalesOrderNumber: 0,
        ConnectionStatus: 0,
        ConnectionStatusString: "",
        Version: "",
        TimezoneOffset: null,
        DST: 0,
        UpdateRequired: false
}

export default function EndUserInstanceList(props: EndUserInstanceListProps): ReactElement {
        const [isModalOpen, setModalOpen] = useState(false);
        const [isResetPasswordModalOpen, setResetPasswordModalOpen] = useState(false);
        const [isOTPModalOpen, setOTPModalOpen] = useState(false);
        const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
        const [modalTitle, setModalTitle] = useState("");
        const [otpModalTitle, setOTPModalTitle] = useState("");
        const [resetPasswordModalTitle, setResetPasswordModalTitle] = useState("");
        const [deleteModalTitle, setDeleteModalTitle] = useState("");
        const dispatch = useDispatch<AppDispatch>();
        const { endUserInstancesLoading, endUserInstances } = useSelector((state: RootState) => state.endUserInstances);
        const { authUser } = useSelector((state: RootState) => state.auth);
        const { endUserInstanceLocations } = useSelector((state: RootState) => state.endUserInstanceLocations);

        const [isRequestingOTP, setRequestingOTP] = useState(false);

        const [selectedEndUserInstanceLocation, setEndUserInstanceLocation] = useState<EndUserInstanceLocationType | undefined>(initialEndUserInstanceLocation);
        const [selectedResetAdminPassword, setResetAdminPassword] = useState<ResetAdminPasswordType | undefined>(initialResetAdminPassword);

        const [endUserInstanceExpanded, setEndUserInstanceExpanded] = useState(props.macAddressSearchResult.EndUserInstanceId == 0 ? false : true);
        const [endUserInstanceIDSelected, setEndUserInstanceIDSelected] = useState(props.macAddressSearchResult.EndUserInstanceId);

        const { endUserInstanceLocation } = useSelector((state: RootState) => state.endUserInstanceLocation);

        const endUserInstanceClick = (event: React.MouseEvent<HTMLDivElement>) => {
                props.endUserInstanceClick(event);
        }

        const loadInstance = (event: React.MouseEvent<HTMLDivElement>) => {
                const id = parseInt(event.currentTarget.id);
                const endUserInstance = endUserInstances.find(a => a.Id === id);
                window.open(endUserInstance?.Link, '_blank');
        }

        const generateSHA256 = (input: string): string => {
                return CryptoJS.SHA256(input).toString(CryptoJS.enc.Hex);
        }

        const handleSubmitRequestCode = async (event: React.MouseEvent<HTMLDivElement>) => {
                try {
                        setRequestingOTP(true);
                        const data: OTPType = {
                                EmailId: authUser?.EmailId,
                        };
                        initialResetAdminPassword.Id = parseInt(event.currentTarget.id);
                        setResetAdminPassword(initialResetAdminPassword);
                        const response = await apiClient.post('otp/request-otp', data);
                        if (response.status == 200) {
                                setOTPModalOpen(true);
                        }
                } catch (responseError: any) {
                        console.log(responseError);
                }
                finally {
                        setRequestingOTP(false);
                }
        }

        const handleSubmitVerifyCode = async (data: OTPCodeType) => {
                try {
                        setRequestingOTP(true);
                        data.EmailId = authUser?.EmailId;
                        const response = await apiClient.post('otp/verify-otp', data);
                        if (response.status == 200) {
                                setOTPModalOpen(false);
                                setResetPasswordModalOpen(true);
                                setResetPasswordModalTitle("Reset Admin Password");
                        }
                } catch (responseError: any) {
                        console.log(responseError);
                }
                finally {
                        setRequestingOTP(false);
                }
        }

        const handleSubmit = async (data: ResetAdminPasswordType) => {
                try {
                        const hashedPassword = generateSHA256(data.Password);
                        const RequestEndUserInstanceAdminPassword = {
                                Id: data.Id,
                                AdminPassword: hashedPassword
                        }
                        const response = await apiClient.post('endUserInstance/updateEndUserInstanceAdminPassword', RequestEndUserInstanceAdminPassword);
                        if (response.status == 200) {
                                setResetPasswordModalOpen(false);

                        }
                } catch (responseError: any) {
                        console.log(responseError);
                }
        }

        const endUserInstanceLocationClicked = (event: React.MouseEvent<HTMLDivElement>) => {
                const id = event.currentTarget.id;
                setModalOpen(true);
                if (id === "") {
                        setModalTitle("Add EndUserInstanceLocation");
                        initialEndUserInstanceLocation.EndUserInstanceId = endUserInstanceIDSelected;
                        setEndUserInstanceLocation(initialEndUserInstanceLocation);
                }
                else {
                        setModalTitle("Edit EndUserInstanceLocation");
                        setEndUserInstanceLocation(endUserInstanceLocations.find(a => a.Id === Number(id)));
                }
        }

        const closeEndUserInstanceLocations = async () => {
                setEndUserInstanceExpanded(false);
                setEndUserInstanceIDSelected(0);
        }

        const listEndUserInstanceLocations = async (endUserInstanceID: number) => {
                setEndUserInstanceExpanded(true);
                setEndUserInstanceIDSelected(endUserInstanceID);
                await dispatch(getEndUserInstanceLocationsByEndUserInstanceID(endUserInstanceID));
        }

        const handleSubmitEndUserInstanceLocation = async (data: EndUserInstanceLocationType) => {
                if (modalTitle === "Add EndUserInstanceLocation") {
                        await dispatch(createEndUserInstanceLocation(data));
                }
                else if (modalTitle === "Edit EndUserInstanceLocation") {
                        await dispatch(updateEndUserInstanceLocation(data));
                }
        }

        const deleteEndUserInstanceClick = (event: React.MouseEvent<HTMLDivElement>) => {
                props.deleteEndUserInstanceClick(event);
        }

        const DeleteEndUserInstanceLocationClicked = (event: React.MouseEvent<HTMLDivElement>) => {
                const id = event.currentTarget.id;
                const endUserInstanceLocation = endUserInstanceLocations.find(e => e.Id === Number(id));
                setEndUserInstanceLocation(endUserInstanceLocation);
                setDeleteModalTitle("Delete EndUserInstanceLocation");
                setDeleteModalOpen(true);
        }

        const DeleteEndUserInstanceLocation = async (doDelete: boolean) => {
                if (doDelete) {
                        await dispatch(deleteEndUserInstanceLocation(selectedEndUserInstanceLocation));
                }
                setDeleteModalTitle("");
                setDeleteModalOpen(false);
        }

        useEffect(() => {
                if (endUserInstanceLocation.Id !== 0) {
                        setModalOpen(false);
                        dispatch(insertEndUserInstanceLocation(endUserInstanceLocation));
                        dispatch(endUserInstanceLocationReset());
                }
                if (props.macAddressSearchResult.EndUserInstanceId != 0) {
                        listEndUserInstanceLocations(props.macAddressSearchResult.EndUserInstanceId);
                }
        }, [endUserInstanceLocation.Id, props])

        return <>
                {
                        endUserInstancesLoading || isRequestingOTP ? <Spinner title="Loading" /> :
                                <div className="flex flex-col gap-2 shadow-[0px_0px_5px_rgba(0,0,0,0.25)] overflow-auto p-4">
                                        <h3 className="sticky top-0 bg-blue-200 shadow-[0px_0px_5px_rgba(0,0,0,0.25)]">
                                                <div className="grid grid-cols-[1fr_14fr_1fr_1fr] gap-1">
                                                        <div>
                                                                {authUser?.EmailId === "admin@dsxinc.com" ?
                                                                        <div></div>
                                                                        :
                                                                        <></>
                                                                }
                                                        </div>
                                                        <div className="grid grid-cols-5 p-4 font-semibold text-blue-700">
                                                                <p className="text-center">Status</p>
                                                                <p className="text-center">Cloud ID</p>
                                                                <p className="text-center">Name</p>
                                                                <p className="text-center">Number of Doors</p>
                                                                <p className="text-center">Notes</p>
                                                        </div>
                                                        <div>
                                                        </div>
                                                        <div className="flex items-center justify-center cursor-pointer">
                                                                <div className="flex items-center justify-center cursor-pointer shadow-[0px_0px_5px_rgba(0,0,0,0.25)] hover:shadow-[0px_0px_5px_rgba(0,0,0,0.5)]" onClick={endUserInstanceClick}>
                                                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="green" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="feather feather-plus-square"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><line x1="12" y1="8" x2="12" y2="16"></line><line x1="8" y1="12" x2="16" y2="12"></line></svg>
                                                                </div>
                                                        </div>
                                                </div>
                                        </h3>
                                        {
                                                endUserInstances.map((item) =>
                                                        <div className="grid grid-rows-[1fr_auto]">
                                                                <div className="grid grid-cols-[1fr_14fr_1fr_1fr] gap-1 border-b border-gray-400 p-1">
                                                                        <div>
                                                                                {authUser?.EmailId === "admin@dsxinc.com" ?
                                                                                        <div className="cursor-pointer w-fit">
                                                                                                <div onClick={deleteEndUserInstanceClick} id={item?.Id.toString()} key={item.Id}>
                                                                                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="red" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="feather feather-trash-2"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>
                                                                                                </div>
                                                                                        </div>
                                                                                        :
                                                                                        <div></div>
                                                                                }
                                                                        </div>
                                                                        <div className="grid grid-cols-5 cursor-pointer p-2 hover:bg-gray-300" id={item.Id.toString()} key={item.Id} onClick={endUserInstanceClick}>

                                                                                {
                                                                                        item.Enabled ?
                                                                                                <div className="flex items-center justify-center">
                                                                                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="green" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-check-circle"><path stroke="green" d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
                                                                                                </div>
                                                                                                :
                                                                                                <div className="flex items-center justify-center">
                                                                                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="red" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-x-circle"><circle stroke="red" cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>
                                                                                                </div>
                                                                                }
                                                                                <p className="text-center">{item.EndUserInstanceId}</p>
                                                                                <p className="text-center">{item.Name}</p>
                                                                                <p className="text-center">{item.NumberOfDoors}</p>
                                                                                <p className="text-center">{item.Notes}</p>
                                                                        </div>
                                                                        <div className="flex flex-row gap-4 items-center">
                                                                                {authUser?.EmailId === "admin@dsxinc.com" ?
                                                                                        <div className="cursor-pointer text-blue-700" onClick={handleSubmitRequestCode} id={item.Id.toString()}>
                                                                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="feather feather-lock"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
                                                                                        </div>
                                                                                        :
                                                                                        <div>

                                                                                        </div>
                                                                                }
                                                                                <div className="cursor-pointer text-blue-700" onClick={loadInstance} id={item.Id.toString()}>
                                                                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="feather feather-external-link"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>
                                                                                </div>
                                                                        </div>
                                                                        {endUserInstanceExpanded && endUserInstanceIDSelected === item.Id ?
                                                                                <div className="flex items-center justify-center cursor-pointer shadow-[0px_0px_5px_rgba(0,0,0,0.25)] hover:shadow-[0px_0px_5px_rgba(0,0,0,0.5)]" onClick={() => { closeEndUserInstanceLocations() }}>
                                                                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="red" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="feather feather-arrow-up-circle"><circle cx="12" cy="12" r="10"></circle><polyline points="16 12 12 8 8 12"></polyline><line x1="12" y1="16" x2="12" y2="8"></line></svg>
                                                                                </div>
                                                                                :
                                                                                <div className="flex items-center justify-center cursor-pointer shadow-[0px_0px_5px_rgba(0,0,0,0.25)] hover:shadow-[0px_0px_5px_rgba(0,0,0,0.5)]" onClick={() => { listEndUserInstanceLocations(item.Id) }}>
                                                                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="green" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="feather feather-arrow-down-circle"><circle cx="12" cy="12" r="10"></circle><polyline points="8 12 12 16 16 12"></polyline><line x1="12" y1="8" x2="12" y2="16"></line></svg>
                                                                                </div>
                                                                        }

                                                                </div>
                                                                {endUserInstanceExpanded && endUserInstanceIDSelected === item.Id ?
                                                                        <div>
                                                                                <EndUserInstanceLocationList endUserInstanceLocationList={endUserInstanceLocations} endUserInstanceLocationClick={endUserInstanceLocationClicked} deleteEndUserInstanceLocationClick={DeleteEndUserInstanceLocationClicked} />
                                                                        </div>
                                                                        :
                                                                        <></>
                                                                }
                                                        </div>

                                                )
                                        }

                                </div >
                }
                {isDeleteModalOpen ?
                        <DeleteModal title={deleteModalTitle} delete={DeleteEndUserInstanceLocation} isOpen={isDeleteModalOpen} onClose={() => setDeleteModalOpen(false)} >Do you want to delete Dealer: {selectedEndUserInstanceLocation?.LocationName} ?</DeleteModal>
                        :
                        <></>
                }
                {isResetPasswordModalOpen ?
                        <ResetAdminPasswordModal title={resetPasswordModalTitle} resetAdminPassword={selectedResetAdminPassword} isOpen={isResetPasswordModalOpen} onClose={() => setResetPasswordModalOpen(false)} onSubmit={handleSubmit} />
                        :
                        <></>
                }
                {isOTPModalOpen ?
                        <OTPModal title={otpModalTitle} isOpen={isOTPModalOpen} onClose={() => setOTPModalOpen(false)} onSubmit={handleSubmitVerifyCode} />
                        :
                        <></>
                }
                {
                        isModalOpen ?
                                <EndUserInstanceLocationModal endUserInstanceID={endUserInstanceIDSelected.toString()} title={modalTitle} selectedEndUserInstanceLocation={selectedEndUserInstanceLocation} isOpen={isModalOpen} onClose={() => setModalOpen(false)} onSubmit={handleSubmitEndUserInstanceLocation} />
                                :
                                <></>
                }
        </>
}
